/**
 * ShareFayreButton — shared component for both FayreDetailPage and
 * SupplierFayresPage. Frictionless: tries the Web Share API first
 * (native mobile share sheet), falls back to clipboard copy with a
 * toast. Fires POST /fayres/{id}/share to get the canonical ref-tagged
 * URL + log the event for attribution. Never opens a modal.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Share, Lightning } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ShareFayreButton = ({
  fayreId,
  fayreName,
  variant = 'supplier',      // "supplier" | "inline"
  onShared,
  'data-testid': testid = 'share-fayre-btn',
}) => {
  const [busy, setBusy] = useState(false);

  const share = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/fayres/${fayreId}/share`);
      // Build absolute URL — backend returns a relative path when
      // FRONTEND_ORIGIN isn't configured (common in preview).
      const absolute = data.share_url.startsWith('http')
        ? data.share_url
        : `${window.location.origin}${data.share_url}`;
      const shareText = `${fayreName || 'Wedding Fayre'} — meet 20+ wedding suppliers in one place`;

      let succeeded = false;
      // Try native share sheet first (best UX on mobile)
      if (navigator.share) {
        try {
          await navigator.share({ title: fayreName, text: shareText, url: absolute });
          succeeded = true;
        } catch (_e) {
          // User cancelled — treat as no-op, fall through to clipboard
        }
      }
      if (!succeeded) {
        // Clipboard fallback with toast confirmation
        try {
          await navigator.clipboard.writeText(absolute);
          succeeded = true;
          toast.success('Link copied — share it on Instagram or WhatsApp to get enquiries');
        } catch (_e) {
          // Last-resort fallback for very old browsers / no clipboard
          toast(absolute);
        }
      } else {
        toast.success('Share it on Instagram or WhatsApp to get enquiries');
      }
      if (onShared) onShared(data);
    } catch (e) {
      toast.error('Could not generate share link');
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'inline') {
    // Small hero button — used on FayreDetailPage next to meta chips
    return (
      <button
        onClick={share}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black border border-[#B89762]/40/30 bg-[#B89762]/30/10 text-[#C29D52]/85 hover:bg-[#B89762]/30/20 disabled:opacity-50"
        data-testid={testid}
      >
        <Share size={12} weight="fill" />
        {busy ? 'Sharing…' : 'Share'}
      </button>
    );
  }
  // Supplier variant — full-width CTA with "→ get more enquiries" copy
  return (
    <button
      onClick={share}
      disabled={busy}
      className="w-full mt-2 py-2 rounded-lg text-[11px] font-black flex items-center justify-center gap-1 border border-[#B89762]/40/30 bg-[#B89762]/30/5 hover:bg-[#B89762]/30/10 text-[#C29D52]/85 disabled:opacity-50"
      data-testid={testid}
    >
      <Lightning size={11} weight="fill" />
      {busy ? 'Sharing…' : 'Share fayre → get more enquiries'}
    </button>
  );
};

/**
 * useFayreRefAttribution — fires a one-shot landing log when a
 * `?ref=...` query-param is present on the URL. Idempotent per page
 * load. Silent failure (attribution is opt-in, never blocks render).
 */
export const logFayreLanding = async (fayreId) => {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get('ref') || '').trim();
    if (!ref) return;
    await axios.post(`${API}/fayres/${fayreId}/landed`, { ref });
  } catch (_e) {
    /* silent */
  }
};

export default ShareFayreButton;
